<template>
  <div class="formulario-modelo">
    <h1 class="titulo-modelo">{{ nombreModelo }}</h1>
    <div class="divider"></div>

    <section v-if="componentes.length">
      <div v-for="comp in componentes" :key="comp.id_componente" class="marco">
        <!-- Título del componente padre -->
        <div class="componente-titulo">
          <h2>Componente {{ comp.ordinal }} - {{ comp.nombre }}</h2>
          <span class="peso">Peso: {{ comp.peso ?? '' }}</span>
        </div>

        <table class="tabla-modelo">
          <thead>
            <tr>
              <!-- Columnas dinámicas: nombre + peso por cada nivel -->
              <template v-for="n in nivelesArray.length" :key="n">
                <th>Componente nivel {{ n }}</th>
                <th>Peso</th>
              </template>
              <th>Indicador</th>
              <th>Peso</th>
              <th>Instrumento</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(fila, filaIndex) in filasHorizontales(comp)" :key="fila.key">
              <!-- Primera columna: componente padre unificada con rowspan -->
              <template v-if="filaIndex === 0">
                <td :rowspan="filasHorizontales(comp).length" class="col-padre">
                  {{ comp.nombre }}
                </td>
                <td :rowspan="filasHorizontales(comp).length" class="col-padre">
                  {{ comp.peso ?? '' }}
                </td>
              </template>

              <!-- Render dinámico de hijos (ruta sin el padre) -->
              <template v-for="(nombre, idx) in fila.componentesRuta.slice(1)" :key="idx">
                <td>{{ nombre }}</td>
                <td>{{ fila.componentesPesos[idx+1] ?? '' }}</td>
              </template>

              <!-- Indicador -->
              <td>{{ fila.indicadorNombre || '-' }}</td>
              <td>{{ fila.indicadorPeso ?? '-' }}</td>

              <!-- Instrumento -->
              <td>{{ fila.instrumentoHerramienta || '-' }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Divider uniforme -->
        <div class="tabla-divider"></div>
      </div>
    </section>

    <section v-else>
      <p>Cargando información del modelo...</p>
    </section>
  </div>
</template>

<script lang="ts" setup>
/**
 * Vista consolidada de un modelo con su estructura e indicadores.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `ModeloCompleto/:id_modelo/:id_version` (ModeloCompleto) — permiso `modelos.ver`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useModelo } from '@/store/GestionModelo'
import { useComponentes } from '@/store/GestionComponentes'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'

import type Componente from '@/interfaces/Componente'
import type Instrumento from '@/interfaces/Instrumetos'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import { podarArbolInactivo } from '@/utils/estructuraActiva'

const route = useRoute()
const idModelo = Number(route.params.id_modelo ?? NaN)
const idVersion = Number(route.params.id_version ?? NaN)

const storeModelo = useModelo()
const storeComponentes = useComponentes()
const storeVersion = useGestionVersiones()
const indicadoresStore = useGestionIndicadores()
const instrumentosStore = useGestionInstrumento()
const indicadorInstrumentoStore = useGestionIndicadorInstrumentos()
const tipoStore = useGestionTipoComponente()

const nombreModelo = ref('')
const componentes = ref<Componente[]>([])

const feedback = ref('')
const feedbackEsPositivo = ref(false)
const loadingInicial = ref(false)
const nivelesArray = ref<string[]>([])
const idComponenteRaiz = ref<number | null>(null)

// Tipo de fila horizontal con ruta completa de componentes
interface FilaHorizontal {
  key: string
  nivel: number
  componentesRuta: string[]            
  componentesPesos: (number|null)[]    
  rowCounts: number[]                  
  indicadorNombre?: string
  indicadorPeso?: number | null
  instrumentoHerramienta?: string
}

onMounted(async () => {
  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    feedback.value = 'ID de modelo inválido.'
    feedbackEsPositivo.value = false
    return
  }

  loadingInicial.value = true
  try {
    // Todas las cargas independientes en paralelo
    const [modelo, raiz, activos] = await Promise.all([
      storeModelo.getModeloById(idModelo),
      storeComponentes.cargarRaizPorModelo(idModelo),
      storeVersion.getIdsComponentesActivos(idVersion),
      indicadoresStore.cargarTodos(),
      instrumentosStore.cargarTodos(),
      indicadorInstrumentoStore.getRelaciones(),
      tipoStore.getTipos()
    ])

    if (modelo?.niveles) nivelesArray.value = limpiarNiveles(modelo.niveles)
    nombreModelo.value = modelo?.nombre ?? ''

    if (!raiz) {
      feedback.value = 'No se encontró el componente raíz.'
      feedbackEsPositivo.value = false
      return
    }

    idComponenteRaiz.value = raiz.id_componente ?? null

    if (idComponenteRaiz.value != null) {
      const ok = await storeComponentes.cargarArbolDesdeRaiz(idComponenteRaiz.value!, nivelesArray.value)
      if (!ok) {
        feedback.value = 'No fue posible cargar la versión y sus componentes.'
        feedbackEsPositivo.value = false
      } else {
        const raizCompleta = storeComponentes.componentes.find(c => c.id_componente === idComponenteRaiz.value)
        if (raizCompleta?.children) {
          componentes.value = podarArbolInactivo(raizCompleta.children, activos)
        }
      }
    }

  } catch (e: any) {
    feedback.value = e?.message || 'Error al cargar datos iniciales.'
    feedbackEsPositivo.value = false
  } finally {
    loadingInicial.value = false
  }
})

// Helper para obtener instrumentos de un indicador
function instrumentosPorIndicadorFn(id_indicador: number): Instrumento[] {
  const relaciones = indicadorInstrumentoStore.relaciones.filter(r => r.id_indicador === id_indicador)
  return relaciones
    .map(r => instrumentosStore.instrumentos.find(i => i.id_instrumento === r.id_instrumento))
    .filter((i): i is Instrumento => !!i)
}

// Construye filas horizontales con ruta completa de componentes (nombre + peso + rowCounts)
function filasHorizontales(
  comp: Componente,
  nivel = 0,
  ruta: string[] = [],
  pesos: (number|null)[] = []
): FilaHorizontal[] {
  const filas: FilaHorizontal[] = []
  const nuevaRuta = [...ruta, comp.nombre]
  const nuevosPesos = [...pesos, comp.peso ?? null]

  const indicadores = indicadoresStore.indicadores.filter(i => i.id_componente === comp.id_componente)

  const baseFila = {
    componentesRuta: nuevaRuta,
    componentesPesos: nuevosPesos,
    rowCounts: [] as number[]
  }

  if (indicadores.length === 0) {
    filas.push({
      key: `comp-${comp.id_componente}-n${nivel}`,
      nivel,
      ...baseFila
    })
  } else {
    for (const ind of indicadores) {
      const instrumentos = instrumentosPorIndicadorFn(ind.id_indicador!)
      if (instrumentos.length === 0) {
        filas.push({
          key: `comp-${comp.id_componente}-ind-${ind.id_indicador}-n${nivel}`,
          nivel,
          ...baseFila,
          indicadorNombre: ind.enunciado,
          indicadorPeso: ind.peso_opinion ?? null
        })
      } else {
        for (const inst of instrumentos) {
          filas.push({
            key: `comp-${comp.id_componente}-ind-${ind.id_indicador}-inst-${inst.id_instrumento}-n${nivel}`,
            nivel,
            ...baseFila,
            indicadorNombre: ind.enunciado,
            indicadorPeso: ind.peso_opinion ?? null,
            instrumentoHerramienta: inst.herramienta
          })
        }
      }
    }
  }

  const hijos = comp.children ?? []
  for (const hijo of hijos) {
    filas.push(...filasHorizontales(hijo as Componente, nivel + 1, nuevaRuta, nuevosPesos))
  }

  // Calcular rowCounts: cuántas filas ocupa cada componente en cada nivel
  if (filas.length > 0) {
    const counts: number[] = nuevaRuta.map(name =>
      filas.filter(f => f.componentesRuta.includes(name)).length
    )
    filas.forEach(f => { f.rowCounts = counts })
  }

  return filas
}
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  width: 95%;
  max-height: 100%;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

.titulo-modelo {
  font-size: 1.8rem;
  font-weight: 600;
  color: #0e69a0; 
  margin-bottom: 1rem;
}

.divider {
  border-bottom: 2px solid #949393;
  margin: 12px 0 24px;
}

.marco {
  margin-bottom: 40px;
}

.componente-titulo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #cce6ff;
  color: #0e69a0;
  padding: 1rem 1.25rem;
  margin-bottom: 14px;
  border-radius: 6px;
}

.componente-titulo h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.componente-titulo .peso {
  font-weight: 600;
}

.tabla-modelo {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.tabla-modelo th,
.tabla-modelo td {
  border: 1px solid #ccc;
  padding: 12px 14px;
  word-wrap: break-word;
  text-align: left;
}

.tabla-modelo th {
  background: #eea222;
  color: #fff;
  text-align: center;
}

.tabla-modelo td {
  background-color: #fff6e5;
  color: #333;
}


.col-padre {
  background-color: #cce6ff; 
  font-weight: 600;
  color: #0e69a0;
  vertical-align: middle; 
  text-align: center; 
}

.tabla-divider {
  margin-top: 20px;
  height: 6px;
  border-radius: 4px;
  background: rgba(0,0,0,0.1); 
}
</style>